import { useState, useEffect, useCallback } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Container, Divider, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DDLogo from "../assets/DD_LOGO_TRANSPARENT_S.png";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
  Checkbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { useProductContext } from "../hooks/useProductContext";
import { useAuthStore } from "../stores/zustand";

// Components
import AuthPageWrapper from "../components/AuthPageWrapper";

const policyAgreementinitialStyle = {
  display: "flex",
  gap: "15px",
  mt: 2,
  padding: "15px",
};
const policyAgreementWrongStyle = {
  display: "flex",
  gap: "15px",
  mt: 2,
  padding: "15px",
  border: "solid red",
  borderWidth: "2px",
};

function SignUp() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthData } = useAuthStore();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [openEmailVerification, setOpenEmailVerification] = useState(false);
  const [language, setLanguage] = useState("de");
  const [policyAgreement, setPolicyAgreement] = useState(false);
  const [policyAgreementStyle, setPolicyAgreementStyle] = useState(
    policyAgreementinitialStyle,
  );

  const isMobile = useMediaQuery({ maxWidth: 767 });
  const productContext = useProductContext() || "corporate";

  // Get product type from navigation state (passed from SignIn) or route context
  const productType = location.state?.productType || productContext;

  const validateForm = (firstName, lastName, email, password) => {
    const errors = {};
    if (!firstName?.trim()) errors.firstName = t("firstNameRequired");
    if (!lastName?.trim()) errors.lastName = t("lastNameRequired");
    if (!email?.trim()) {
      errors.email = t("emailRequired") || "Email is required";
    } else if (!validateEmail(email)) {
      errors.email = t("invalidEmail");
    }
    if (!password?.trim()) errors.password = t("passwordRequired");
    return errors;
  };

  const getBackRoute = () => {
    const path = location.pathname;
    if (path === "/register-corporate") return "/corporate";
    if (path === "/register-physio") return "/physio";
    if (path === "/register-physio-user") return "/physio-user";
    if (path === "/register-course") return "/course";
    return "/login";
  };

  // Listen for language changes from Electron menu

  useEffect(() => {
    if (window.electronAPI?.language) {
      const cleanup = window.electronAPI.language.onChange((newLanguage) => {
        i18n.changeLanguage(newLanguage);
        setLanguage(newLanguage);
      });

      return cleanup; // Cleanup on unmount
    }
  }, [i18n]);

  const validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  const handleEmailVerificationClose = () => {
    setOpenEmailVerification(false);
    navigate("/");
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogMessage("");
  };

  const handleSubmitPhysio = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    const firstName = data.get("firstName");
    const lastName = data.get("lastName");
    const email = data.get("email");
    const password = data.get("password");
    const current_product = "physio_therapist";

    if (!policyAgreement) {
      setPolicyAgreementStyle(policyAgreementWrongStyle);
      return;
    }
    const errors = validateForm(firstName, lastName, email, password);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      if (errors.email) setEmailError(errors.email);
      return;
    }
    setFieldErrors({});

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_FIREBASE_API_HOST}/api/auth/register`,
        {
          firstName,
          lastName,
          email,
          password,
          language,
          current_product,
        },
        {
          withCredentials: true,
        },
      );

      // Update authStore with user data
      setAuthData({
        ...response.data,
        isEmailVerified: response.data.auth.isEmailVerified ?? false,
      });

      // Check if email is not verified
      if (response.data.auth.isEmailVerified !== true) {
        setOpenEmailVerification(true);
      } else {
        navigate("/physio-video-selection");
      }
    } catch (error) {
      console.error(error);
      const status = error.response?.data?.status;
      setDialogMessage(status === "USER_ALREADY_EXISTS" ? t("userAlreadyExistsError") : t("anRegistrationErrorOccurred"));
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    const firstName = data.get("firstName");
    const lastName = data.get("lastName");
    const email = data.get("email");
    const password = data.get("password");

    if (!policyAgreement) {
      setPolicyAgreementStyle(policyAgreementWrongStyle);
      return;
    }
    const errors = validateForm(firstName, lastName, email, password);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      if (errors.email) setEmailError(errors.email);
      return;
    }
    setFieldErrors({});

    setLoading(true);
    try {
      const registrationPayload = {
        firstName,
        lastName,
        email,
        password,
        language,
      };

      if (productType) {
        registrationPayload.current_product = productType;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_FIREBASE_API_HOST}/api/auth/register`,
        registrationPayload,
        {
          withCredentials: true, // Backend will set cookies
        },
      );

      if (response.data.status === "USER_ALREADY_EXISTS") {
        setEmailError(t("userAlreadyExists"));
        return;
      }

      const { status } = response.data;

      if (status === "SUCCESS") {
        // Update authStore with user data
        setAuthData({
          ...response.data,
          isEmailVerified: response.data.auth.isEmailVerified ?? false,
        });

        if (response.data.auth.isEmailVerified !== true) {
          setOpenEmailVerification(true);
        } else if (productType === "physio_user") {
          navigate("/home/library");
        } else {
          navigate("/checkout");
        }
      } else if (status === "USER_ALREADY_EXISTS") {
        setEmailError(t("userAlreadyExists"));
      }
    } catch (error) {
      console.error(error);
      const status = error.response?.data?.status;
      setDialogMessage(status === "USER_ALREADY_EXISTS" ? t("userAlreadyExistsError") : t("anRegistrationErrorOccurred"));
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageToggle = useCallback(() => {
    setLanguage(prev => prev === "en" ? "de" : "en");
  }, []);

  function Copyright(props) {
    const { t } = useTranslation();

    return (
      <Typography
        variant="body2"
        color="#063bd0"
        marginTop={"8px"}
        align="center"
        {...props}
      >
        {t("copyright")} ©
        <Link color="inherit" href={process.env.REACT_APP_FIREBASE_BASE_URL}>
          &nbsp;Desk Dynamics
        </Link>{" "}
        {new Date().getFullYear()}
        {"."}
      </Typography>
    );
  }

  return (
    <AuthPageWrapper isMobile={isMobile}>
      <Container component="main" maxWidth="lg" sx={{ height: "100vh" }}>
        <CssBaseline />
        {/* Registration Loading Dialog */}
        <Dialog
          open={loading}
          PaperProps={{
            sx: {
              borderRadius: 3,
              px: 4,
              py: 3,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 3,
              minWidth: 260,
            },
          }}
          slotProps={{
            backdrop: { sx: { backdropFilter: "blur(4px)" } },
          }}
        >
          <Box
            component="img"
            src={DDLogo}
            alt="Loading"
            sx={{
              width: 52,
              height: 52,
              animation: "spin 1.2s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            {t("registering")}
          </Typography>
        </Dialog>

        <Dialog
          open={openEmailVerification}
          onClose={handleEmailVerificationClose}
        >
          <DialogTitle>{t("weSentAnEmail")}</DialogTitle>
          <DialogActions>
            <Button onClick={handleEmailVerificationClose} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
        <Grid container sx={{ height: "100%" }}>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: { xs: "none", sm: "flex" },
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              p: 8,
              position: "sticky",
              top: 0,
              height: "100vh",
              alignSelf: "flex-start",
            }}
          >
            <Typography variant="h3" gutterBottom sx={{ color: "#fff" }}>
              {productContext === "preventive_course"
                ? t("hiAndWelcomePreventive")
                : productContext === "corporate"
                  ? t("hiAndWelcome")
                  : productContext.includes("physio")
                    ? t("hiAndWelcomePhysio")
                    : t("hiAndWelcome")}{" "}
              <b>
                {" "}
                {productContext === "preventive_course"
                  ? t("product_namePreventive")
                  : productContext === "corporate"
                    ? t("product_name")
                    : productContext.includes("physio")
                      ? t("product_namePhysio")
                      : t("product_name")}{" "}
              </b>
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "#fff" }}>
              <b>
                {productContext === "preventive_course"
                  ? t("authSubtitlePreventive")
                  : productContext.includes("physio")
                    ? t("authSubtitlePhysio")
                    : t("authSubtitle")}
              </b>
            </Typography>
            <Typography
              sx={{ color: "#fff", marginTop: "24px", alignSelf: "flex-start" }}
            >
              {productContext === "preventive_course"
                ? t("authDescriptionPreventive")
                : productContext === "physio_therapist"
                  ? t("authDescriptionPhysio")
                  : productContext === "physio_user"
                    ? t("authDescriptionPhysioUser")
                    : t("authDescription")}
            </Typography>
            <Typography
              sx={{ color: "#fff", marginTop: "24px", alignSelf: "flex-start" }}
            >
              {t("tryOut")}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: isMobile ? 1 : 8,
              overflow: "auto",
            }}
          >
            <div
              style={{ width: "100%", padding: "36px" }}
              className={
                isMobile ? "white-background-mobile" : "white-background"
              }
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", alignSelf: "flex-start", mb: 1 }}>
                  <IconButton onClick={() => navigate(getBackRoute())} size="small" sx={{ color: "#063bd0", mr: 1 }}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography
                    component="h1"
                    variant="h6"
                    style={{ color: "#063bd0" }}
                  >
                    {t("signUp")}
                  </Typography>
                </Box>
                <Box
                  component="form"
                  noValidate
                  onSubmit={
                    policyAgreement
                      ? productType === "physio_therapist"
                        ? handleSubmitPhysio
                        : handleSubmit
                      : null
                  }
                  sx={{ mt: 3 }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        autoComplete="given-name"
                        name="firstName"
                        required
                        fullWidth
                        id="firstName"
                        label={t("firstName")}
                        error={!!fieldErrors.firstName}
                        helperText={fieldErrors.firstName || ""}
                        onChange={() => setFieldErrors(p => ({ ...p, firstName: "" }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        id="lastName"
                        label={t("lastName")}
                        name="lastName"
                        autoComplete="family-name"
                        error={!!fieldErrors.lastName}
                        helperText={fieldErrors.lastName || ""}
                        onChange={() => setFieldErrors(p => ({ ...p, lastName: "" }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label={t("emailAddress")}
                        name="email"
                        autoComplete="email"
                        error={!!emailError}
                        helperText={emailError}
                        onChange={(e) => {
                          setEmailError(validateEmail(e.target.value) ? "" : t("invalidEmail"));
                          setFieldErrors(p => ({ ...p, email: "" }));
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        name="password"
                        label={t("password")}
                        type="password"
                        id="password"
                        autoComplete="new-password"
                        error={!!fieldErrors.password}
                        helperText={fieldErrors.password || ""}
                        onChange={() => setFieldErrors(p => ({ ...p, password: "" }))}
                      />
                    </Grid>
                    <Grid
                      component="label"
                      container
                      alignItems="center"
                      spacing={0.5}
                      sx={{ paddingLeft: 2, justifyContent: "flex-end", mt: 1 }}
                    >
                      <Grid item>
                        <Typography variant="body1">EN</Typography>
                      </Grid>
                      <Grid item>
                        <Switch
                          checked={language === "de"}
                          onChange={handleLanguageToggle}
                        />
                      </Grid>
                      <Grid item>
                        <Typography variant="body1">DE</Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Button
                    type={policyAgreement ? "submit" : "button"}
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2, float: "right", width: "50%" }}
                    onClick={
                      policyAgreement
                        ? () =>
                            setPolicyAgreementStyle(policyAgreementinitialStyle)
                        : () =>
                            setPolicyAgreementStyle(policyAgreementWrongStyle)
                    }
                  >
                    {t("signUp")}
                  </Button>
                </Box>
                <Box sx={policyAgreementStyle}>
                  <Checkbox
                    value={policyAgreement}
                    onChange={() => setPolicyAgreement(!policyAgreement)}
                    required
                  />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    align="center"
                    sx={{}}
                  >
                    {t("agreeToPrivacy")}{" "}
                    <Link
                      href={`${process.env.REACT_APP_FIREBASE_BASE_URL}/terms/`}
                      target="_blank"
                      rel="noopener"
                    >
                      {t("privacyPolicy")}
                    </Link>
                    ,{" "}
                    <Link
                      href={`${process.env.REACT_APP_FIREBASE_BASE_URL}/additional-terms/`}
                      target="_blank"
                      rel="noopener"
                    >
                      {t("additionalPrivacyPolicy")}
                    </Link>
                    , and{" "}
                    <Link
                      href={`${process.env.REACT_APP_FIREBASE_BASE_URL}/privacy-policy/`}
                      target="_blank"
                      rel="noopener"
                    >
                      {t("dataprotection")}
                    </Link>
                    .
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start", // Aligns items to the left
                  maxWidth: 400,
                  width: "100%",
                  mt: 4, // Adds margin to the top for spacing
                }}
              >
                <Divider
                  sx={{ width: "100%", height: "2px", bgcolor: "divider" }}
                />
                <Typography
                  sx={{
                    color: "#063bd0",
                    mt: 2,
                    alignSelf: "flex-start",
                    marginTop: "28px",
                  }}
                >
                  {t("alreadyAccount")}
                </Typography>
                <Typography
                  sx={{
                    color: "#063bd0",
                    alignSelf: "flex-start",
                    marginBottom: "8px",
                  }}
                >
                  {t("logInHere")}
                </Typography>
                <Button
                  onClick={() => {
                    // Navigate back to appropriate login route based on product context
                    if (productType === "corporate") {
                      navigate("/corporate");
                    } else if (productType === "preventive_course") {
                      navigate("/course");
                    } else if (productType === "physio_therapist") {
                      navigate("/physio");
                    } else if (productType === "physio_user") {
                      navigate("/physio-user");
                    } else {
                      navigate("/login");
                    }
                  }}
                  variant="outlined"
                  sx={{
                    width: "50%",
                    mt: 2,
                    mb: 2,
                    "&:hover": {
                      bgcolor: "transparent",
                      color: "#ffffff",
                      boxShadow: "none",
                      border: "1px solid #05b67f",
                    },
                    backgroundColor: "transparent",
                    color: "#063bd0",
                    border: "1px solid #063bd0",
                    alignSelf: "flex-end", // Aligns the button to the right
                    boxShadow: "none", // Remove box-shadow if any
                  }}
                >
                  {t("signIn")}
                </Button>
              </Box>
              <Copyright />
            </div>
          </Grid>
          <Dialog open={dialogOpen} onClose={handleDialogClose} PaperProps={{ sx: { borderRadius: "15px", minWidth: 320 } }}>
            <DialogTitle sx={{ px: 4, pt: 3, pb: 1 }}>{t("registrationError")}</DialogTitle>
            <DialogContent sx={{ px: 4, pb: 2 }}>
              <DialogContentText>{dialogMessage}</DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 4, pb: 3 }}>
              <Button onClick={handleDialogClose}>{t("close")}</Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Container>
    </AuthPageWrapper>
  );
}

export default SignUp;
